import React, { useEffect, useMemo, useState } from "react";
import { Versions } from "@app/shared/src/api/api.gen";
import { Device } from "@app/shared/src/models";
import { CorePackageRow, corePackageRows, pendingUpgradeNames } from "@app/shared/src/helpers/packageUpgrades";
import { Btn, Modal, Note, Spinner, Tag, formatAgo, formatDateTime } from "@app/shared/src/components_v2";
import { siteLabel } from "../../helpers/deviceLabels";
import { useLoad } from "../../hooks/useLoad";

/**
 * Pick which packages a truck should install on its next check-in.
 *
 * The unit tells the server what apt has installed and what each package could move to; the
 * server turns the names chosen here into a package-update command and the device runs apt on
 * its next check-in. That is the same path the main dashboard's Upgrade tab uses — the old
 * "fetch this firmware build" command it replaced is no longer honoured by the firmware, which
 * is why a plain Update button that queued one silently did nothing.
 *
 * Pending upgrades start ticked: a director who pressed Update wants the unit current, and the
 * boxes are there to leave something out, not to hunt for what to put in. A package the unit has
 * never had starts unticked — a first install is a different decision from taking an update.
 */
export const UpgradePackagesDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    device: Device;
    /** Fired after the server accepts the request, so the caller can refresh the truck's row. */
    onQueued?: () => void;
}> = ({ open, onClose, device, onQueued }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const [selected, setSelected] = useState<Record<string, boolean>>({});

    const report = useLoad(
        () => (open ? Versions.latestByDeviceId(device.id) : Promise.resolve(undefined)),
        [open, device.id],
    );

    // Not gated on a report arriving: a first rollout only reaches a unit that never had the package.
    const rows = useMemo(() => corePackageRows(report.data?.packageVersions), [report.data]);
    const changeable = rows.filter((row) => row.upgradableTo !== undefined);

    // Reset to "everything pending" whenever a fresh report arrives or the dialog reopens, so a
    // choice made for the last truck never carries over to this one.
    useEffect(() => {
        if (!open) return;
        setError(undefined);
        setSelected(Object.fromEntries(pendingUpgradeNames(rows).map((name) => [name, true])));
    }, [open, rows]);

    const chosen = changeable.filter((row) => selected[row.name]).map((row) => row.name);
    const toggle = (row: CorePackageRow) => setSelected((prev) => ({ ...prev, [row.name]: !prev[row.name] }));

    const run = async () => {
        if (chosen.length === 0) return;
        setBusy(true);
        setError(undefined);
        try {
            await Versions.updateDevice(device.id, chosen);
            onQueued?.();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not queue the update");
        } finally {
            setBusy(false);
        }
    };

    // The server refuses a second command while one is waiting, so say so before the click
    // rather than after it — but keep the list visible: what would change is still worth reading.
    const blocked = !!device.hasPendingDeviceCommands;
    const reportedAt = report.data?.dateReported;
    // Judged on the report, not the rows: a known core package the unit lacks is always offered,
    // so the rows are never empty even when the unit has told us nothing at all.
    const noReport = !report.isLoading && !report.error && !report.data?.packageVersions?.length;

    return (
        <Modal
            open={open}
            onClose={busy ? () => undefined : onClose}
            title={`Update ${siteLabel(device)}`}
            subtitle={
                reportedAt
                    ? `Packages as the unit reported them ${formatAgo(reportedAt)} (${formatDateTime(reportedAt)})`
                    : "Packages as the unit last reported them"
            }
            footer={
                <>
                    <Btn tone="quiet" onClick={onClose} disabled={busy}>
                        Cancel
                    </Btn>
                    <Btn onClick={run} disabled={busy || blocked || chosen.length === 0}>
                        {busy
                            ? "Queuing…"
                            : chosen.length === 0
                              ? "Install selected"
                              : `Install ${chosen.length} package${chosen.length === 1 ? "" : "s"}`}
                    </Btn>
                </>
            }
        >
            {blocked && (
                <Note tone="warn">
                    This unit already has a command waiting for its next check-in. Once that has gone through, come
                    back and queue the update.
                </Note>
            )}

            {report.isLoading ? (
                <div className="flex items-center gap-3 py-4 text-[13px] text-[var(--muted)]">
                    <Spinner />
                    Reading the unit's package report…
                </div>
            ) : report.error ? (
                <Note tone="crit">{report.error}</Note>
            ) : (
                <>
                    {noReport && (
                        <Note>
                            This unit has not reported its installed packages yet, so only packages that can be
                            installed fresh are listed. It sends a report shortly after it powers up.
                        </Note>
                    )}
                    <div className="rounded-[12px] border border-[var(--line)]">
                        {rows.map((row) => {
                            const pending = row.upgradableTo !== undefined;
                            return (
                                <label
                                    key={row.name}
                                    className={`flex items-center gap-3 border-t border-[var(--line)] px-4 py-2.5 first:border-t-0 ${
                                        pending ? "cursor-pointer" : ""
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="h-3.5 w-3.5 flex-none accent-[var(--accent)] disabled:opacity-40"
                                        checked={!!selected[row.name]}
                                        disabled={!pending || busy}
                                        onChange={() => toggle(row)}
                                        aria-label={`Install ${row.name}`}
                                    />
                                    <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">{row.name}</span>
                                    <span className="flex-none text-[12.5px] tabular-nums text-[var(--muted)]">
                                        {row.installed ? row.currentVersion ?? "—" : "not installed"}
                                    </span>
                                    {pending ? (
                                        <span className="flex-none text-[12.5px] font-semibold tabular-nums text-[var(--safe)]">
                                            → {row.upgradableTo}
                                        </span>
                                    ) : (
                                        <Tag tone="mute">Current</Tag>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </>
            )}

            {!report.isLoading && !report.error && !noReport && changeable.length === 0 && (
                <p className="text-[12.5px] text-[var(--muted)]">Everything on this unit is already current.</p>
            )}

            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};
